import { Injectable } from '@angular/core';
import { CybermetrieService, Evenement } from '@oel/js-cybermetrie';
import { CybermetrieEnum } from '../../constantes/cybermetrie.enum';
import { ContexteModificationEnum } from '../../constantes/contexte-modification.enum';

@Injectable({
  providedIn: 'root',
})
export class CybermetrieCoordonneesService {
  constructor() {}

  actionClick(label: string, champExtra?: string, champExtra2?: string): void {
    this.envoyerCybermetrieEvenement(
      CybermetrieEnum.CATEGORIE_GESTION_COORDONNEES,
      CybermetrieEnum.ACTION_CLIC,
      label,
      champExtra ? this.getExtraAttribut(champExtra, champExtra2) : undefined,
    );
  }

  getExtraAttribut(champExtra: string, champExtra2?: string): Map<string, string> {
    let extraAttribut = new Map<string, string>([[CybermetrieEnum.EXTRA_ATTRIBUT, champExtra]]);
    return champExtra && champExtra2
      ? extraAttribut.set(CybermetrieEnum.EXTRA_ATTRIBUT_2, champExtra2)
      : extraAttribut;
  }

  interractionsForms(action: string, label: string): void {
    this.envoyerCybermetrieEvenement(
      CybermetrieEnum.CATEGORY_INTERACTIONS_FORMES,
      action,
      CybermetrieEnum.LABEL_MODIFIER_ADRESSE_DGAG + label,
    );
  }

  transactionDebuter(label: string, champExtra?: string): void {
    // timeout pour s'assurer que l'événement est bien capté après le chargement de la page.
    // Sans le timeout on a extraAtribut indésirable
    const extraAttribut = champExtra
      ? new Map<string, string>([[CybermetrieEnum.EXTRA_ATTRIBUT, champExtra]])
      : undefined;

    setTimeout(() => {
      this.envoyerCybermetrieEvenement(
        CybermetrieEnum.CATEGORIE_TRANSACTION,
        CybermetrieEnum.ACTION_DEBUTE,
        label,
        extraAttribut,
      );
    }, 500);
  }

  transactionEtape(label: string, champExtra?: string): void {
    const extraAttribut = champExtra
      ? new Map<string, string>([[CybermetrieEnum.EXTRA_ATTRIBUT, champExtra]])
      : undefined;
    // timeout pour s'assurer que l'événement est bien capté après le chargement de la page.
    // Sans le timeout on a extraAtribut indésirable
    setTimeout(() => {
      this.envoyerCybermetrieEvenement(
        CybermetrieEnum.CATEGORIE_TRANSACTION,
        CybermetrieEnum.ACTION_ETAPE,
        label,
        extraAttribut,
      );
    }, 500);
  }

  transactionComplete(
    label: string,
    context: ContexteModificationEnum,
    champExtra?: string,
    champExtra2?: string,
  ): void {
    // timeout pour s'assurer que l'événement est bien capté après le chargement de la page.
    // Sans le timeout on a extraAtribut indésirable
    if (context === ContexteModificationEnum.ADRESSE) {
      setTimeout(() => {
        this.envoyerCybermetrieEvenement(
          CybermetrieEnum.CATEGORIE_TRANSACTION,
          CybermetrieEnum.ACTION_COMPLETE,
          label,
          champExtra2 ? this.getExtraAttribut(champExtra2, champExtra) : undefined,
        );
      }, 500);
    } else {
      setTimeout(() => {
        this.envoyerCybermetrieEvenement(
          CybermetrieEnum.CATEGORIE_TRANSACTION,
          CybermetrieEnum.ACTION_COMPLETE,
          label,
          champExtra ? this.getExtraAttribut(champExtra) : undefined,
        );
      }, 500);
    }
  }

  transactionEof(texteErreurs: string[]) {
    const separator = ' | ';
    const erreurs = new Map<string, string>();
    erreurs.set(CybermetrieEnum.MESSAGE_ERREUR, texteErreurs.join(separator));
    setTimeout(() => {
      this.envoyerCybermetrieEvenement(
        CybermetrieEnum.CATEGORIE_TRANSACTION,
        CybermetrieEnum.ACTION_EOF,
        CybermetrieEnum.LABEL_MODIFIER_ADRESSE,
        erreurs,
      );
    }, 500);
  }

  transactionErreurUtilisateur(label: string, texteErreurs: string[]): void {
    const texteErreursNormalize = texteErreurs.map((texte) =>
      texte.normalize('NFD').replaceAll(/[\u0300-\u036f]/g, ''),
    );
    const separator = ' | ';
    const erreurs = new Map<string, string>();
    erreurs.set(CybermetrieEnum.MESSAGE_ERREUR, texteErreursNormalize.join(separator));
    this.envoyerCybermetrieEvenement(
      CybermetrieEnum.CATEGORIE_TRANSACTION,
      CybermetrieEnum.ACTION_ERREUR_UTILISATEUR,
      label,
      erreurs,
    );
  }

  transactionErreurTechnique(
    contexteModification: ContexteModificationEnum | undefined,
    texteErreurs: string[],
  ): void {
    const separator = ' | ';
    const erreurs = new Map<string, string>();
    erreurs.set(CybermetrieEnum.MESSAGE_ERREUR, texteErreurs.join(separator));
    if (contexteModification) {
      this.envoyerCybermetrieEvenement(
        CybermetrieEnum.CATEGORIE_TRANSACTION,
        CybermetrieEnum.ACTION_ERREUR_TECHNIQUE,
        this.getErreurTechniqueLabel(contexteModification),
        erreurs,
      );
    } else {
      this.envoyerCybermetrieEvenement(
        CybermetrieEnum.CATEGORIE_ERREUR,
        CybermetrieEnum.ACTION_ERREUR_TECHNIQUE,
        CybermetrieEnum.LABEL_MODIFIER_COORDONNEES_ERREUR,
        erreurs,
      );
    }
  }

  private getErreurTechniqueLabel(contexteModification: ContexteModificationEnum): string {
    return {
      adresse: CybermetrieEnum.LABEL_MODIFIER_ADRESSE,
      courriel: CybermetrieEnum.LABEL_MODIFIER_COURRIEL,
      telephone: CybermetrieEnum.LABEL_MODIFIER_TELEPHONE,
    }[contexteModification];
  }

  private envoyerCybermetrieEvenement(
    eventCategory: string,
    eventAction: string,
    eventLabel: string,
    dimensionsPersonnalisees?: Map<string, string>,
    valeur?: number,
  ): void {
    CybermetrieService.envoyerEvenement(
      {
        event: Evenement.InterractionsPages,
        category: eventCategory,
        action: eventAction,
        label: eventLabel,
        value: valeur,
        nonInteraction: false,
      },
      dimensionsPersonnalisees,
    );
  }
}
